import React, { useCallback, useEffect, useState } from 'react';
import './index.css';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Form, ModalBody } from 'reactstrap';

import { useFileImport } from './hooks/useFileImport';
import { useImportFields } from './hooks/useImportFields';
import { useImportBroadcastList } from './hooks/useImportBroadcastList';
import { processImportData } from './utils/importProcessors';

import FileUpload from './components/FileUpload';
import FieldMapping from './components/FieldMapping';
import ImportFormFooter from './components/ImportFormFooter';
import ModalConfirmation from 'components/Modal/ConfirmationModal';

/**
 * Componente genérico de importação de dados para diferentes entidades
 *
 * @param {Object} props - Propriedades do componente
 * @param {boolean} props.isOpen - Controla se o modal está visível
 * @param {Function} props.setIsOpen - Função para alternar a visibilidade do modal
 * @param {string|number} props.recordTypeId - ID do tipo de registro
 * @param {Function} props.importService - Serviço para importar dados
 * @param {Array} props.defaultFields - Campos obrigatórios padrão da entidade
 * @param {string} props.filterEntity - Identificador para memorizar filtros
 * @param {Function} props.getCustomFields - Função para carregar campos customizados
 * @param {number} props.maxRecords - Número máximo de registros permitidos (padrão: 50000)
 * @param {boolean} props.showUpdateExistingOption - Controla a exibição da opção de atualizar registros existentes
 * @param {Function} props.validateData - Função para validar os dados processados
 * @param {Function} props.onImportSuccess - Função de callback para tratar o sucesso da importação
 */
const ImportDataForm = ({
  isOpen,
  setIsOpen,
  recordTypeId,
  importService,
  validateImportService,
  defaultFields,
  filterEntity,
  getCustomFields,
  maxRecords = 50000,
  showUpdateExistingOption = false,
  onImportSuccess,
  type = 'crmEntities',
  broadcastListId
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [dataToImport, setDataToImport] = useState([]);
  const [validationMessage, setValidationMessage] = useState('');
  const [acceptedToImport, setAcceptedToImport] = useState([]);

  const { fileInfo, handleFileChange, changeHasHeader, resetFileInfo } =
    useFileImport(maxRecords);

  const importDataByEntity = async (type, dataDTO) => {
    let payload = {};

    if (dataDTO.length === 0) {
      toast.warn('Nenhum dado válido para ser importado');
      return;
    }
    if (type === 'broadcastList') {
      payload = {
        parsedData: dataDTO,
        useContextParams: dataDTO.every(
          (contact) =>
            contact?.contextParams && typeof contact.contextParams === 'object'
        )
      };
      return importService({ ...payload, recordTypeId });
    } else if (type === 'crmEntities') {
      payload = { data: dataDTO, updateExisting: showUpdateExistingOption };
      return importService(recordTypeId, payload);
    } else if (type === 'whatsAppBlockList') {
      const formattedData = dataDTO.map((item) => ({
        ddi: item.telephone?.ddi || item.ddi,
        telephone: item.telephone?.number || item.telephone,
        reason: item.reason
      }));

      payload = { data: formattedData };
      return importService(payload);
    } else if (type === 'accountEntities') {
      payload = { data: dataDTO, updateExisting: showUpdateExistingOption };
      return importService(recordTypeId, payload);
    }
  };

  const {
    fields,
    protocolFields,
    templateFields,
    optionalFields,
    option,
    setOption,
    setFields,
    isLoading,
    validationErrors,
    setValidationErrors,
    useBrazilianDDI,
    handleUseBrazilianDDI,
    onAddField,
    onRemoveField,
    validateForm,
    validateFields,
    saveImportFields,
    resetFieldsState
  } = useImportFields({
    defaultRequiredFields: defaultFields,
    recordTypeId,
    getCustomFields,
    filterEntity
  });
  const { loadBroadcastListFields } = useImportBroadcastList(broadcastListId);

  const handlerFieldsProtocol = async () => {
    const { inputsToMap } = await loadBroadcastListFields();
    const newFields = new Map(fields.map((i) => [i.id, i]));

    inputsToMap.forEach((newInput) => {
      if (newInput?.id && !newFields.has(newInput?.id)) {
        newFields.set(newInput.id, newInput);
      }
    });

    setFields(Array.from(newFields.values()));
  };

  const { handleSubmit, control, setValue, reset } = useForm();

  useEffect(() => {
    if (!isOpen) {
      resetFieldsState();
    }
  }, [isOpen, resetFieldsState]);

  useEffect(() => {
    if (isOpen && type === 'broadcastList') {
      handlerFieldsProtocol();
    }
  }, [fileInfo]);

  const handleFieldChange = useCallback(
    (fieldName, value) => {
      if (value && validationErrors[fieldName]) {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
      }
      setValue(fieldName, value);
    },
    [setValue, validationErrors, setValidationErrors]
  );

  const resetForm = useCallback(() => {
    resetFileInfo();
    resetFieldsState();
    reset();
    setIsOpen(false);
    setUpdateExisting(false);
    setAcceptedToImport([]);
  }, [resetFileInfo, resetFieldsState, reset, setIsOpen]);

  const handleConfirmImport = async () => {
    setIsConfirmModalOpen(false);
    setIsSubmitting(true);

    try {
      let response;

      if (type === 'accountEntities') {
        if (acceptedToImport.length === 0) {
          toast.warn('Nenhuma conta válida para importar');
          resetForm();
          return;
        }
        response = await importDataByEntity(type, acceptedToImport);
      }

      const importedCount =
        type === 'accountEntities'
          ? acceptedToImport.length
          : dataToImport.length;
      toast.success(`${importedCount} conta(s) importada(s) com sucesso`);

      if (onImportSuccess) {
        onImportSuccess(
          response,
          type === 'accountEntities' ? acceptedToImport : dataToImport
        );
      }
      saveImportFields(fields);
      resetForm();
    } catch (error) {
      const msg =
        error?.response?.data?.message || 'Erro ao confirmar importação';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = async (data) => {
    if (isSubmitting) return;

    const errors = validateForm(data);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    const { errorsFields, sanitizeData } = validateFields({
      data,
      fileData: fileInfo.data,
      useBrazilianDDI,
      fields
    });

    setIsSubmitting(true);

    try {
      let dataDTO = processImportData({
        data,
        fields,
        fileData: sanitizeData,
        useBrazilianDDI,
        type
      });

      // Essa validação é necessária pois é preciso validar o DDI juntamente com o número
      dataDTO = dataDTO.filter((row) => {
        let returnValue = true;
        const keys = Object.keys(row);
        keys.forEach((key) => {
          let item = row[key];
          if (
            returnValue &&
            typeof item === 'object' &&
            item !== null &&
            item?.number &&
            item?.ddi === '55'
          ) {
            returnValue = !(
              item?.number.trim().length !== 10 &&
              item?.number.trim().length !== 11
            );
          }
        });
        return returnValue;
      });
      let response;
      let payload = {};
      if (type === 'accountEntities') {
        payload = {
          data: dataDTO,
          updateExisting: showUpdateExistingOption
        };

        response = await validateImportService(recordTypeId, payload);
      } else {
        response = await importDataByEntity(type, dataDTO);
      }

      if (type === 'accountEntities' && response) {
        const responseData = response.data?.data || response.data || response;
        const { accepted, rejected } = responseData;

        setAcceptedToImport(accepted || []);
        setDataToImport(dataDTO);

        if (rejected && rejected.length > 0) {
          const rejectedList = rejected
            .map(
              (item) => `
              <li class="error-item-minimal">
                <span class="error-description-minimal">
                  ${item.name || 'Nome não informado'} - ${item.rejectionReason}
                </span>
              </li>
            `
            )
            .join('');

          const quantidadeValidas = accepted.length;
          const quantidadeInvalidas = rejected.length;

          const textoInvalidas =
            quantidadeInvalidas === 1
              ? '1 conta não pode ser importada'
              : `${quantidadeInvalidas} contas não podem ser importadas`;

          const textoValidas =
            quantidadeValidas === 1
              ? '1 conta válida será importada'
              : `${quantidadeValidas} contas válidas serão importadas`;

          setValidationMessage(`
            <h3>Atenção: ${textoInvalidas}</h3>
            <ul class="error-list">${rejectedList}</ul>
            <br/>
            <h4>${textoValidas}</h4>
            <p>Deseja prosseguir com a importação apenas das contas válidas?</p>
          `);
          setIsConfirmModalOpen(true);
          return;
        }

        if (accepted && accepted.length > 0) {
          await importDataByEntity(type, accepted);
          toast.success(`${accepted.length} contas importadas com sucesso`);
          saveImportFields(fields);
          resetForm();
        } else {
          toast.warn('Nenhuma conta válida para importar');
          setIsSubmitting(false);
        }

        return;
      }

      if (errorsFields.length > 0) {
        setDataToImport(dataDTO);
        const msgFields = `
          <ul class="error-list">
            ${errorsFields
              .map(
                (invalidField) => `
                  <li class="error-item-minimal">
                    <span class="error-line-minimal">Linha ${
                      invalidField.row + 1
                    }</span>,
                    <span class="error-column-minimal" title="${
                      invalidField.herderName
                    }">coluna ${invalidField?.herderName}</span>:
                    <span class="error-description-minimal">${
                      invalidField?.message
                    }</span>
                  </li>
                `
              )
              .join('')}
          </ul>
        `;
        setValidationMessage(
          `<h3>As seguintes informações apresentam inconsistências e não serão importadas:</h3>
          <br/>
          ${msgFields}
          <h5>${dataDTO.length} contato(s) serão enviados para importação</h5>
          `
        );
        setIsConfirmModalOpen(true);
        return;
      }

      if (onImportSuccess) onImportSuccess(response, dataDTO);
      else toast.success(`${dataDTO.length} registros importados com sucesso`);

      saveImportFields(fields);
      resetForm();
    } catch (error) {
      const msg = error?.response?.data?.message || 'Erro ao importar dados';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ModalBody>
        <FileUpload
          handleFileChange={handleFileChange}
          fileInfo={fileInfo}
          changeHasHeader={changeHasHeader}
          isDisabled={isSubmitting || isLoading}
        />

        <Form id="import-data-form" onSubmit={handleSubmit(onSubmit)}>
          {fileInfo.file && (
            <FieldMapping
              fields={fields}
              protocolFields={protocolFields}
              templateFields={templateFields}
              optionalFields={optionalFields}
              onRemoveField={onRemoveField}
              option={option}
              setOption={setOption}
              onAddField={onAddField}
              useBrazilianDDI={useBrazilianDDI}
              handleUseBrazilianDDI={handleUseBrazilianDDI}
              control={control}
              handleFieldChange={handleFieldChange}
              fileColumns={fileInfo.columns}
              isSubmitting={isSubmitting}
              validationErrors={validationErrors}
              showUpdateExistingOption={showUpdateExistingOption}
              updateExisting={updateExisting}
              setUpdateExisting={setUpdateExisting}
            />
          )}
        </Form>
      </ModalBody>

      <ImportFormFooter
        onCancel={resetForm}
        isSubmitting={isSubmitting}
        hasFile={!!fileInfo.file}
        isLoading={isLoading}
      />

      <ModalConfirmation
        isModalOpen={isConfirmModalOpen}
        onModalToggle={setIsConfirmModalOpen}
        unmountOnClose
        idResource={dataToImport}
        deleteResource={handleConfirmImport}
        message={validationMessage}
        modalContainerClasses="modal-lg"
        messageContainsHtml
      />
    </>
  );
};

export default ImportDataForm;
